
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { TimePeriod } from "@/components/dashboard/TimeFilter";

export interface GlobalStats {
  totalUsers: number;
  totalSessions: number;
  totalMeditationTime: number;
  totalFitnessSessions: number;
  totalFitnessTime: number;
}

export function useGlobalStats(timePeriod: TimePeriod = "all") {
  const [stats, setStats] = useState<GlobalStats>({
    totalUsers: 0,
    totalSessions: 0,
    totalMeditationTime: 0,
    totalFitnessSessions: 0,
    totalFitnessTime: 0,
  });

  const fetchGlobalStats = async () => {
    console.log(`Fetching global stats for period: ${timePeriod}...`);
    
    try {
      // Get total users count from the global leaderboard view instead of users table
      const { data: leaderboardData, error: leaderboardError } = await supabase
        .from('global_leaderboard')
        .select('user_id');
      
      // Don't block fitness stats if leaderboard view is missing or restricted by RLS
      let totalUsersCount = 0;
      if (leaderboardError) {
        console.warn("Leaderboard view unavailable, continuing without user count:", leaderboardError.message);
      } else {
        totalUsersCount = leaderboardData?.length || 0;
      }
      console.log("Total users count from leaderboard:", totalUsersCount);
      
      let completedSessions: any[] = [];
      
      if (timePeriod === "all") {
        // Get ALL completed meditation sessions
        const { data: allSessions, error: sessionsError } = await supabase
          .rpc('get_all_completed_sessions');
          
        if (sessionsError) {
          console.error("Error fetching all sessions:", sessionsError);
          throw sessionsError;
        }
        
        completedSessions = allSessions || [];
      } else {
        let startDate: Date | null = null;
        const now = new Date();
        
        // Set the appropriate start date based on the selected time period
        startDate = new Date();
        
        switch(timePeriod) {
          case "day":
            startDate.setHours(0, 0, 0, 0);
            break;
          case "week":
            startDate.setDate(now.getDate() - 7);
            break;
          case "month":
            startDate.setMonth(now.getMonth() - 1);
            break;
          case "year":
            startDate.setFullYear(now.getFullYear() - 1);
            break;
        }
        
        console.log(`Using start date: ${startDate.toISOString()} for period: ${timePeriod}`);
        
        // Get filtered sessions for the selected time period
        const { data: filteredSessions, error: filteredError } = await supabase
          .rpc('get_filtered_completed_sessions', { 
            start_date: startDate.toISOString() 
          });
          
        if (filteredError) {
          console.error("Error fetching filtered sessions:", filteredError);
          throw filteredError;
        }
        
        completedSessions = filteredSessions || [];
        console.log(`Filtered sessions for period ${timePeriod}:`, completedSessions.length);
      }

      // Calculate total meditation time
      const totalMeditationTime = completedSessions.reduce((sum, session) => {
        return sum + (session.duration || 0);
      }, 0);

      // Get fitness sessions using database functions to bypass RLS
      let fitnessData: any[] = [];
      
      if (timePeriod === "all") {
        // Get ALL fitness sessions
        const { data: allFitnessSessions, error: fitnessError } = await supabase
          .rpc('get_all_fitness_sessions');
          
        if (fitnessError) {
          console.error("Error fetching all fitness sessions:", fitnessError);
          throw fitnessError;
        }
        
        fitnessData = allFitnessSessions || [];
      } else {
        let startDate: Date | null = null;
        const now = new Date();
        startDate = new Date();
        
        switch(timePeriod) {
          case "day":
            startDate.setHours(0, 0, 0, 0);
            break;
          case "week":
            startDate.setDate(now.getDate() - 7);
            break;
          case "month":
            startDate.setMonth(now.getMonth() - 1);
            break;
          case "year":
            startDate.setFullYear(now.getFullYear() - 1);
            break;
        }
        
        // Get filtered fitness sessions for the selected time period
        const { data: filteredFitnessSessions, error: filteredFitnessError } = await supabase
          .rpc('get_filtered_fitness_sessions', { 
            start_date: startDate.toISOString() 
          });
          
        if (filteredFitnessError) {
          console.error("Error fetching filtered fitness sessions:", filteredFitnessError);
          throw filteredFitnessError;
        }
        
        fitnessData = filteredFitnessSessions || [];
        console.log(`Filtered fitness sessions for period ${timePeriod}:`, fitnessData.length);
      }
      
      const totalFitnessSessions = fitnessData.length;
      const totalFitnessTime = fitnessData.reduce((sum, session) => {
        return sum + (session.duration || 0);
      }, 0);

      console.log(`Stats for ${timePeriod}: Users: ${totalUsersCount}, Meditation Sessions: ${completedSessions.length}, Meditation Time: ${totalMeditationTime}, Fitness Sessions: ${totalFitnessSessions}, Fitness Time: ${totalFitnessTime}`);

      return {
        totalUsers: totalUsersCount,
        totalSessions: completedSessions.length,
        totalMeditationTime,
        totalFitnessSessions,
        totalFitnessTime,
      };
    } catch (error) {
      console.error("Error fetching global stats:", error);
      toast({
        title: "Error loading global stats",
        description: "There was a problem fetching the global statistics.",
        variant: "destructive",
      });
      return {
        totalUsers: 0,
        totalSessions: 0,
        totalMeditationTime: 0,
        totalFitnessSessions: 0,
        totalFitnessTime: 0,
      };
    }
  };

  const { data: globalStats, isLoading, refetch } = useQuery({
    queryKey: ["globalStats", timePeriod],
    queryFn: fetchGlobalStats,
    staleTime: 1000,
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
    retry: 3,
  });

  useEffect(() => {
    if (globalStats) {
      console.log("Updated global stats:", globalStats);
      setStats(globalStats);
    }
  }, [globalStats]);

  return { stats, isLoading, refetch };
}
